// src/hooks/firestore/core.ts
import { useState, useEffect, useCallback, useRef, useMemo, useReducer } from 'react';
import { 
  collection, 
  doc, 
  getDocs, 
  query, 
  onSnapshot,
  DocumentData,
  QueryConstraint,
  limit,
  startAfter,
  collectionGroup,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { trackQuery } from '@/utils/queryTracker';
import { activeSubscriptions, cleanupSubscription, getSubscriptionKey } from './utils/subscriptions';
import { areQueryConstraintsEqual } from './utils/comparisons';

// Firestore state reducer for batched state updates
interface FirestoreState<T> {
  documents: T[] | null;
  error: string | null;
  loading: boolean;
  lastDoc: DocumentData | null;
  hasMore: boolean;
}

type FirestoreAction<T> = 
  | { type: 'LOADING' }
  | { type: 'SUCCESS', payload: { documents: T[], lastDoc: DocumentData | null, hasMore: boolean } }
  | { type: 'ERROR', payload: { error: string } };

function firestoreReducer<T>(state: FirestoreState<T>, action: FirestoreAction<T>): FirestoreState<T> {
  switch(action.type) {
    case 'LOADING':
      return { ...state, loading: true };
    case 'SUCCESS':
      return { 
        documents: action.payload.documents, 
        error: null, 
        loading: false,
        lastDoc: action.payload.lastDoc,
        hasMore: action.payload.hasMore 
      };
    case 'ERROR':
      return { 
        documents: [], 
        error: action.payload.error, 
        loading: false,
        lastDoc: null,
        hasMore: false 
      };
    default:
      return state;
  }
}

// Update interfaces before they're used
export interface UseCollectionReturn<T> {
  documents: T[] | null;
  error: string | null;
  loading: boolean;
  loadMore: () => Promise<void>;
  hasMore: boolean;
  refresh: () => void;
}

export interface UseDocumentReturn<T> {
  document: T | null;
  error: string | null;
  loading: boolean;
  refresh: () => void;
}

/**
 * Hook to fetch a single document with real-time updates
 * @param collectionName - Firestore collection name
 * @param docId - Document ID
 */
export function useDocument<T = DocumentData>(
  collectionName: string, 
  docId: string | undefined
): UseDocumentReturn<T> {
  const [document, setDocument] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  
  const previousValues = useRef({ collectionName, docId });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (loading) {
        setLoading(false);
      }
    }, 8000); // 8 second timeout
    
    return () => clearTimeout(timeoutId);
  }, [loading, collectionName, docId]);

  useEffect(() => {
    if (
      previousValues.current.collectionName === collectionName && 
      previousValues.current.docId === docId &&
      document !== null 
    ) {
      return;
    }
    
    previousValues.current = { collectionName, docId };
    
    if (!docId) {
      setDocument(null);
      setLoading(false);
      return;
    }

    if (!collectionName || collectionName === '') {
      setDocument(null);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    const docRef = doc(db, collectionName, docId);
    
    try {
      const unsubscribe = onSnapshot(docRef, 
        (doc) => {
          if (doc.exists()) {
            const docData = { id: doc.id, ...doc.data() } as unknown as T;
            setDocument(docData);
          } else {
            setDocument(null);
          }
          setLoading(false);
        },
        (err) => {
          console.error(`Error fetching document from ${collectionName}:`, err);
          setError(`Failed to fetch document: ${err.message}`);
          setLoading(false);
          
          if (err.code === 'permission-denied') {
          }
        }
      );

      return () => {
        unsubscribe();
      };
    } catch (e) {
      console.error(`[useDocument - ${collectionName}/${docId}] Error creating snapshot listener:`, e);
      setError(`Failed to create snapshot listener for ${collectionName}/${docId}`);
      setDocument(null);
      setLoading(false);
      return () => {};
    }
  }, [collectionName, docId, document]);

  const refresh = useCallback(() => {
  }, [collectionName, docId]);

  return { document, error, loading, refresh };
}

/**
 * Hook to fetch a collection with optional filters and pagination
 * @param collectionName - Firestore collection name
 * @param queryConstraints - Array of query constraints (where, orderBy, etc.)
 * @param pageSize - Number of documents to fetch per page (only used if usePagination is true)
 * @param usePagination - Whether to enable pagination (defaults to false)
 * @param disabled - Whether to disable the hook
 */

let globalInstanceCounter = 0; // Use a global counter for unique IDs

export function useCollection<T = DocumentData>(
  collectionName: string, 
  queryConstraintsInput?: QueryConstraint[],
  pageSize: number = 100,
  usePagination: boolean = false,
  disabled: boolean = false
): UseCollectionReturn<T> {
  const queryConstraints = useMemo(() => queryConstraintsInput || [], [queryConstraintsInput]);

  const [state, dispatch] = useReducer(firestoreReducer<T>, {
    documents: null,
    error: null,
    loading: true,
    lastDoc: null,
    hasMore: false
  });
  
  const { documents, error, loading, lastDoc, hasMore } = state;

  // Assign a unique, stable ID to this hook instance
  const instanceIdRef = useRef<number | null>(null);
  if (instanceIdRef.current === null) {
    instanceIdRef.current = globalInstanceCounter++;
  }
  const instanceId = instanceIdRef.current; // Stable ID for this instance

  const mountedRef = useRef(true);
  const constraintsRef = useRef<QueryConstraint[]>(queryConstraints);
  
  if (!areQueryConstraintsEqual(constraintsRef.current, queryConstraints)) {
    constraintsRef.current = queryConstraints;
  }
  
  const expectedCountRef = useRef<number | null>(null);
  const stableConstraints = constraintsRef.current;
  
  const isValidCollection = useMemo(() => 
    Boolean(collectionName && collectionName.trim() !== ''), 
    [collectionName]
  );

  const constraintsKey = useMemo(() => 
    JSON.stringify(stableConstraints), 
    [stableConstraints]
  );

  // Generate a subscription key unique to this instance
  const subscriptionKey = useMemo(() => 
    `${getSubscriptionKey(collectionName, constraintsKey)}::${instanceId}`,
    [collectionName, constraintsKey, instanceId] // Include instanceId
  );

  useEffect(() => {
    mountedRef.current = true;
    
    if (!isValidCollection || disabled) {
      if (state.documents === null) { 
        dispatch({ type: 'SUCCESS', payload: { documents: [], lastDoc: null, hasMore: false } });
      }
      return () => {
         mountedRef.current = false; // Ensure cleanup sets mountedRef to false
      };
    }

    if (state.documents === null && !state.loading) {
      dispatch({ type: 'LOADING' });
    }
    
    trackQuery(collectionName);
    
    let unsubscribeSnapshot: (() => void) | null = null;

    try {
      const collectionRef = collection(db, collectionName);
      const effectiveConstraints = [...stableConstraints];
      if (usePagination) {
        effectiveConstraints.push(limit(pageSize));
      }
      const q = query(collectionRef, ...effectiveConstraints);

      const currentInstanceSubscriptionKey = subscriptionKey;

      unsubscribeSnapshot = onSnapshot(q, 
        (snapshot) => {
          if (!mountedRef.current) {
             return; 
          }
          
          const results: T[] = [];
          try {
             snapshot.docs.forEach((doc) => {
              const data = doc.data();
              results.push({ id: doc.id, ...data } as unknown as T);
            });
          } catch (loopError) {
            console.error(`[useCollection - ${collectionName} - ID: ${instanceId}] ERROR DURING DOC PROCESSING LOOP:`, loopError);
            if (!mountedRef.current) return; 
            dispatch({ type: 'ERROR', payload: { error: `Error processing documents for ${collectionName}` } });
            return;
          }
            
          expectedCountRef.current = results.length;
            
          const newLastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
          const newHasMore = usePagination ? snapshot.docs.length === pageSize : false;
            
          if (!mountedRef.current) {
              return;
          }
          dispatch({ 
            type: 'SUCCESS', 
            payload: { 
              documents: results, 
              lastDoc: usePagination ? newLastDoc : null, 
              hasMore: newHasMore 
            } 
          });
        },
        (err) => {
          if (!mountedRef.current) {
             return;
          }
          console.error(`Error fetching ${collectionName} (ID: ${instanceId}, Key: ${currentInstanceSubscriptionKey}):`, err);
          dispatch({ 
            type: 'ERROR', 
            payload: { error: `Failed to fetch ${collectionName}: ${err.message}` } 
          });
            
          if (err.code === 'permission-denied') {
          }
        }
      );
        
      activeSubscriptions.set(currentInstanceSubscriptionKey, unsubscribeSnapshot);

    } catch (e) {
       if (!mountedRef.current) return; 
      console.error(`[useCollection - ${collectionName} - ID: ${instanceId}] Error creating query or listener:`, e);
      dispatch({ 
        type: 'ERROR', 
        payload: { error: `Failed to create query or listener for ${collectionName}` } 
      });
       return () => {
         mountedRef.current = false; 
       };
    }

    return () => {
      const keyToClean = subscriptionKey; 
      cleanupSubscription(keyToClean);
      mountedRef.current = false;
    };

  }, [collectionName, stableConstraints, pageSize, usePagination, isValidCollection, subscriptionKey, instanceId]);

  // Function to load more documents (with cache integration)
  const loadMore = async (): Promise<void> => {
    if (!isValidCollection || !usePagination || !hasMore || !lastDoc || loading) return;
    
    dispatch({ type: 'LOADING' });
    
    try {
      const collectionRef = collection(db, collectionName);
      const q = query(
        collectionRef, 
        ...stableConstraints, 
        startAfter(lastDoc), 
        limit(pageSize)
      );
      
      const snapshot = await getDocs(q);
      
      const newDocs = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      } as unknown as T));
      
      const newLastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : lastDoc;
      const newHasMore = snapshot.docs.length === pageSize;
      
      // Combine existing and new documents
      const allDocs = [...(documents || []), ...newDocs];
      
      dispatch({
        type: 'SUCCESS',
        payload: {
          documents: allDocs,
          lastDoc: newLastDoc,
          hasMore: newHasMore
        }
      });
    } catch (err) {
      console.error(`Error loading more from ${collectionName}:`, err);
      dispatch({
        type: 'ERROR',
        payload: { error: `Failed to load more documents` }
      });
    }
  };

  const refresh = useCallback(() => {
  }, [collectionName, instanceId]);

  return { documents, error, loading, loadMore, hasMore, refresh };
}

/**
 * Hook to fetch a collection group with optional filters and pagination
 * @param collectionGroupName - Firestore collection group name 
 * @param queryConstraints - Array of query constraints (where, orderBy, etc.)
 * @param pageSize - Number of documents to fetch per page (only used if usePagination is true)
 * @param usePagination - Whether to enable pagination (defaults to false)
 */
export function useCollectionGroup<T = DocumentData>(
  collectionGroupName: string, 
  queryConstraintsInput?: QueryConstraint[],
  pageSize: number = 100,
  usePagination: boolean = false
): UseCollectionReturn<T> {
  const queryConstraints = useMemo(() => queryConstraintsInput || [], [queryConstraintsInput]);

  const [state, dispatch] = useReducer(firestoreReducer<T>, {
    documents: null,
    error: null,
    loading: true,
    lastDoc: null,
    hasMore: false
  });
  
  const { documents, error, loading, lastDoc, hasMore } = state;

  // Assign a unique, stable ID to this hook instance
  const instanceIdRef = useRef<number | null>(null);
  if (instanceIdRef.current === null) {
    instanceIdRef.current = globalInstanceCounter++;
  }
  const instanceId = instanceIdRef.current; // Stable ID for this instance

  const mountedRef = useRef(true);
  const constraintsRef = useRef<QueryConstraint[]>(queryConstraints);
  
  if (!areQueryConstraintsEqual(constraintsRef.current, queryConstraints)) {
     constraintsRef.current = queryConstraints;
  }
  
  const stableConstraints = constraintsRef.current;
  
  const isValidCollection = useMemo(() => 
    Boolean(collectionGroupName && collectionGroupName.trim() !== ''), 
    [collectionGroupName]
  );

  const constraintsKey = useMemo(() => 
    JSON.stringify(stableConstraints), 
    [stableConstraints]
  );

  // Generate a subscription key unique to this instance
  const subscriptionKey = useMemo(() => 
    `${getSubscriptionKey(`group-${collectionGroupName}`, constraintsKey)}::${instanceId}`, // Unique key
    [collectionGroupName, constraintsKey, instanceId]
  );

  useEffect(() => {
    mountedRef.current = true;

    if (!isValidCollection) {
       if (state.documents === null) {
          dispatch({ type: 'SUCCESS', payload: { documents: [], lastDoc: null, hasMore: false } });
       }
       return () => {
          mountedRef.current = false; // Ensure cleanup sets mountedRef to false
       };
    }

    if (state.documents === null && !state.loading) {
        dispatch({ type: 'LOADING' });
    }
    
    trackQuery(`group-${collectionGroupName}`);
    
    let unsubscribeSnapshot: (() => void) | null = null;
    
    try {
      const effectiveConstraints = [...stableConstraints];
      if (usePagination) {
        effectiveConstraints.push(limit(pageSize));
      }
      const q = query(collectionGroup(db, collectionGroupName), ...effectiveConstraints);

      const currentInstanceSubscriptionKey = subscriptionKey;

      unsubscribeSnapshot = onSnapshot(q, 
        (snapshot) => {
          if (!mountedRef.current) {
             return;
          }
          
          const results: T[] = [];
          try {
            snapshot.docs.forEach(doc => {
              results.push({ id: doc.id, ...doc.data() } as unknown as T);
            });
          } catch (loopError) {
            console.error(`[useCollectionGroup - ${collectionGroupName} - ID: ${instanceId}] ERROR DURING DOC PROCESSING LOOP:`, loopError);
             if (!mountedRef.current) return; 
            dispatch({ type: 'ERROR', payload: { error: `Error processing documents for ${collectionGroupName}` } });
            return;
          }
          
          const newLastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
          const newHasMore = usePagination ? snapshot.docs.length === pageSize : false;
          
          if (!mountedRef.current) {
              return;
          }
          dispatch({ 
            type: 'SUCCESS', 
            payload: { 
              documents: results, 
              lastDoc: usePagination ? newLastDoc : null, 
              hasMore: newHasMore 
            } 
          });
        },
        (err) => {
          if (!mountedRef.current) {
             return;
          }
          console.error(`Error fetching collection group ${collectionGroupName} (ID: ${instanceId}, Key: ${currentInstanceSubscriptionKey}):`, err);
          dispatch({ 
            type: 'ERROR', 
            payload: { error: `Failed to fetch collection group ${collectionGroupName}: ${err.message}` } 
          });
          
          if (err.code === 'permission-denied') {
          }
        }
      );
      
      activeSubscriptions.set(currentInstanceSubscriptionKey, unsubscribeSnapshot);

    } catch (e) {
       if (!mountedRef.current) return; 
      console.error(`[useCollectionGroup - ${collectionGroupName} - ID: ${instanceId}] Error creating query or listener:`, e);
      dispatch({ 
        type: 'ERROR', 
        payload: { error: `Failed to create query or listener for group ${collectionGroupName}` } 
      });
       return () => {
         mountedRef.current = false; 
       };
    }

    return () => {
      const keyToClean = subscriptionKey; 
      cleanupSubscription(keyToClean);
      mountedRef.current = false;
    };

  }, [collectionGroupName, stableConstraints, pageSize, usePagination, isValidCollection, subscriptionKey, instanceId]);

  // Function to load more documents (with cache integration)
  const loadMore = async (): Promise<void> => {
    if (!isValidCollection || !usePagination || !hasMore || !lastDoc || loading) return;
    
    dispatch({ type: 'LOADING' });
    
    try {
      const q = query(
        collectionGroup(db, collectionGroupName), 
        ...stableConstraints, 
        startAfter(lastDoc), 
        limit(pageSize)
      );
      
      const snapshot = await getDocs(q);
      
      const newDocs = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      } as unknown as T));
      
      const newLastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : lastDoc;
      const newHasMore = snapshot.docs.length === pageSize;
      
      // Combine existing and new documents
      const allDocs = [...(documents || []), ...newDocs];
      
      dispatch({
        type: 'SUCCESS',
        payload: {
          documents: allDocs,
          lastDoc: newLastDoc,
          hasMore: newHasMore
        }
      });
    } catch (err) {
      console.error(`Error loading more from collection group ${collectionGroupName}:`, err);
      dispatch({
        type: 'ERROR',
        payload: { error: `Failed to load more documents` }
      });
    }
  };

  const refresh = useCallback(() => {
  }, [collectionGroupName, instanceId]);

  return { documents, error, loading, loadMore, hasMore, refresh };
} 